import { SignatureEditor } from '@/components/forms/SignatureEditor';
import { SmtpConfigForm } from '@/components/forms/SmtpConfigForm';
import { Button } from '@/components/ui/Button';
import {
  defaultSignatureSanitizeOptions,
  sanitizeHtml,
} from '@/utils/validation/html';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import {
  CreateSenderDto,
  Sender,
  UpdateSenderDto,
} from '../types/sender.types';

// Validation schema
import {
  SANS_BOITE_PROVIDER_ID,
  smtpConfigBaseSchema,
  smtpConfigRefine,
} from '@/schemas/smtp-config.schema';

const senderSchema = z
  .object({
    domain: z.string().optional(),
    useSignature: z.boolean().optional(),
    signature: z.string().optional(),
  })
  .merge(smtpConfigBaseSchema)
  .superRefine(smtpConfigRefine);

type SenderFormData = z.infer<typeof senderSchema>;

interface SenderFormProps {
  sender?: Sender; // For editing
  onSubmit: (data: CreateSenderDto | UpdateSenderDto) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

export const SenderForm = ({
  sender,
  onSubmit,
  onCancel,
  isLoading = false,
}: SenderFormProps) => {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize form with consolidated values
  const form = useForm<SenderFormData>({
    resolver: zodResolver(senderSchema),
    mode: 'onChange', // Validate on change to enable button
    defaultValues: {
      // SMTP Defaults
      smtpProvider: sender?.smtpProvider || '',
      smtpHost: sender?.smtpHost || '',
      // L'API renvoie null sans serveur ; le schéma n'accepte que undefined.
      smtpPort: sender?.smtpPort ?? undefined,
      smtpSsl: sender?.smtpSsl ?? false,
      smtpTls: sender?.smtpTls ?? false,
      smtpUser: sender?.smtpUser || '',
      smtpPassword: '', // Never prefill password
      smtpFromEmail: sender?.smtpFromEmail || sender?.email || '',
      smtpFromName: sender?.smtpFromName || sender?.name || '',

      // Other defaults
      domain: sender?.metadata.domain || '',
      useSignature: sender?.useSignature ?? false,
      signature: sender?.signature || '',
    },
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    watch,
  } = form;

  const watchedSignature = watch('signature');
  const watchedUseSignature = watch('useSignature');
  const watchedSmtpProvider = watch('smtpProvider');
  const isOAuthProvider =
    watchedSmtpProvider === 'microsoft365-oauth' ||
    watchedSmtpProvider === 'gmail-oauth';

  // Extract domain from email if not provided
  const getDomainFromEmail = (email: string) => {
    if (!email) return '';
    const parts = email.split('@');
    return parts.length > 1 ? parts[1] : '';
  };

  const handleFormSubmit = async (data: SenderFormData) => {
    setIsSubmitting(true);
    try {
      if (data.smtpProvider === SANS_BOITE_PROVIDER_ID) {
        const name = (data.smtpFromName || '').trim();
        const email = (data.smtpFromEmail || '').trim();
        // Serveur vidé : sans serveur ni connexion Microsoft/Gmail, l'API envoie
        // par Innov'Paie sous ce nom, avec « Répondre » vers cette adresse.
        // Vider le serveur compte aussi quand on convertit une boîte existante.
        await onSubmit({
          name,
          email,
          domain: getDomainFromEmail(email),
          smtpProvider: SANS_BOITE_PROVIDER_ID,
          smtpHost: '',
          smtpUser: '',
          smtpFromName: name,
          smtpFromEmail: email,
          signature: data.signature
            ? sanitizeHtml(data.signature, defaultSignatureSanitizeOptions)
            : undefined,
          useSignature: data.useSignature ?? false,
        } as CreateSenderDto);
        return;
      }

      // Derive name and email from SMTP config
      const finalName = data.smtpFromName || data.smtpUser || 'Expéditeur';
      const finalEmail = data.smtpFromEmail || data.smtpUser || '';

      const formData = {
        ...data,
        name: finalName,
        email: finalEmail,
        domain: data.domain || getDomainFromEmail(finalEmail),
        signature: data.signature
          ? sanitizeHtml(data.signature, defaultSignatureSanitizeOptions)
          : undefined,
        useSignature: data.useSignature ?? false,
      } as CreateSenderDto;

      await onSubmit(formData);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid =
    isValid && !isOAuthProvider && !isLoading && !isSubmitting;

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
      {/* SMTP Configuration Section */}
      <div>
        <h3 className="mb-4 text-lg font-semibold text-neutral-900">
          {t('senders.form.smtpConfig', 'Envoi des emails')}
        </h3>
        <SmtpConfigForm form={form} showTestButton={true} />
      </div>

      {/* Domain field */}
      {/* <div>
        <label className="mb-2 block text-sm font-medium text-neutral-900">
          <Globe className="mr-2 inline h-4 w-4" />
          {t('senders.form.domain', 'Domain (Optional)')}
        </label>
        <input
          type="text"
          {...register('domain')}
          placeholder={
            watchedEmail ? getDomainFromEmail(watchedEmail) : 'yourdomain.com'
          }
          className="focus:border-primary-500 focus:ring-primary-100 w-full rounded-xl border border-neutral-300 px-4 py-3 transition-colors focus:ring-2"
        />
        <p className="mt-1 text-xs text-neutral-500">
          {t(
            'senders.form.domainHelp',
            'Domain associated with this sender. If not provided, will be extracted from email.'
          )}
        </p>
      </div> */}

      {/* Use Signature toggle */}
      <div className="flex items-center justify-between rounded-xl border border-neutral-200 bg-white p-4">
        <div>
          <p className="text-sm font-medium text-neutral-900">
            {t('senders.form.useSignature', 'Use signature')}
          </p>
          <p className="text-xs text-neutral-500">
            {t(
              'senders.form.useSignatureHelp',
              'Enable to append this HTML signature to all emails sent with this sender'
            )}
          </p>
        </div>
        <label className="relative inline-flex cursor-pointer items-center">
          <input
            type="checkbox"
            className="peer sr-only"
            {...register('useSignature')}
          />
          <div className="peer-focus:ring-primary-100 peer peer-checked:bg-primary-600 h-6 w-11 rounded-full bg-neutral-200 after:absolute after:top-[2px] after:left-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-neutral-300 after:bg-white after:transition-all peer-checked:after:translate-x-full" />
        </label>
      </div>

      {/* Signature editor */}
      <div>
        <label className="mb-2 block text-sm font-medium text-neutral-900">
          {t('senders.form.signature', 'HTML Signature')}
        </label>
        <div
          className={`rounded-xl border ${errors.signature ? 'border-error-500' : 'border-neutral-300'} bg-white p-2`}
        >
          <SignatureEditor
            value={watchedSignature || ''}
            onChange={(html: string) => {
              const syntheticEvent = {
                target: { name: 'signature', value: html },
              } as unknown as React.ChangeEvent<HTMLTextAreaElement>;
              register('signature').onChange(syntheticEvent);
            }}
            disabled={!watchedUseSignature}
            allowRawHtmlPaste
          />
        </div>
        {errors.signature && (
          <p className="text-error-600 mt-1 text-sm">
            {errors.signature.message}
          </p>
        )}
        <p className="mt-1 text-xs text-neutral-500">
          {t(
            'senders.form.signatureHelp',
            'Allowed tags: div, p, span, strong, em, br, table, tr, td. Max length: 2000 characters'
          )}
        </p>
      </div>

      {/* Signature preview */}
      <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-4">
        <p className="mb-2 text-sm font-medium text-neutral-900">
          {t('senders.form.signaturePreview', 'Signature preview')}
        </p>
        {watchedUseSignature && watchedSignature ? (
          <div
            className="prose max-w-none text-sm"
            dangerouslySetInnerHTML={{
              __html: sanitizeHtml(
                watchedSignature,
                defaultSignatureSanitizeOptions
              ),
            }}
          />
        ) : (
          <p className="text-sm text-neutral-500">
            {t('senders.form.noSignatureActive', 'No active signature')}
          </p>
        )}
        <p className="mt-2 text-xs text-neutral-500">
          {t(
            'senders.form.signaturePreviewHelp',
            'Preview may not exactly match email clients rendering'
          )}
        </p>
      </div>

      {/* Form actions */}
      <div className="flex justify-end gap-3">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          {t('common.cancel', 'Cancel')}
        </Button>
        <Button type="submit" disabled={!isFormValid}>
          {isSubmitting ? (
            <>
              <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
              {sender
                ? t('senders.form.updating', 'Updating...')
                : t('senders.form.creating', 'Creating...')}
            </>
          ) : isOAuthProvider ? (
            t('senders.form.oauthRequired', 'Connexion requise')
          ) : sender ? (
            t('senders.form.update', 'Update Sender')
          ) : (
            t('senders.form.create', 'Create Sender')
          )}
        </Button>
      </div>
    </form>
  );
};
